import type { RunVisualArtifact, RunVisualsResponse, RunVisualViewId } from "@/api/run-visuals";
import { RUN_VISUAL_VIEW_ORDER, TORSION_VIEW_TYPES } from "@/api/run-visuals";
import { isStructureBearingType } from "@/components/viz/molecule-structure";

/**
 * Which visualizations a run can offer *right now* — the progressive
 * selector behind the run view's visual panel (flowstack#950 Part B).
 *
 * Kept as pure functions over the visuals index so the three-state logic is
 * testable without mounting a chart, a 3D viewer, or a poll.
 */

/**
 * The three states a view can be in, and they are deliberately three.
 *
 * - `ready` — the data exists; render it.
 * - `pending` — it does not exist YET, and the run could still produce it.
 *   During a live run this is the *normal* state for most views, which is why
 *   it is offered-but-disabled rather than hidden: hiding it makes the panel
 *   look like it will never have plots, and showing it enabled makes an empty
 *   chart look like a bug.
 * - `absent` — the run is over and never produced it. A `.frcmod`, a log or a
 *   topology has nothing to draw and that is a legitimate outcome, not a
 *   failure.
 *
 * A FOURTH state — "produced but failed to load" — is deliberately NOT here:
 * it belongs to the fetch of a specific artifact, not to the index, and each
 * view surfaces it from its own query. Collapsing "not yet" into "failed" (or
 * either into "absent") is exactly the flowstack#923 defect, and during a live
 * run *most* things are legitimately not-yet.
 */
export type RunVisualAvailability = "ready" | "pending" | "absent";

export interface RunVisualView {
  id: RunVisualViewId;
  label: string;
  availability: RunVisualAvailability;
  /**
   * The artifact this view would render, when it has one. `null` for a view
   * that is not `ready`, and for the torsion view — whose data is fetched
   * per-fragment from the AFFDO routes rather than from a single artifact.
   */
  artifact: RunVisualArtifact | null;
  /** The node the molecule view is pinned to, or `null` for the run subject. */
  pinnedToNode?: string | null;
  /** How many structure inputs that node consumed; >1 for a fanned node. */
  siblingInputCount?: number;
}

const VIEW_LABELS: Record<RunVisualViewId, string> = {
  molecule: "Molecule",
  torsion: "Torsion plots",
};

/**
 * Pick the artifact to show as "the molecule this run is about".
 *
 * Order of preference, and each step is a claim about meaning rather than a
 * tie-break:
 *
 * 1. A structure-bearing artifact the run CONSUMED from outside itself — the
 *    ligand the user submitted. That is the run's subject, and it is available
 *    from the first moment the run exists, which is what lets the molecule
 *    stay visible for the *whole* live run rather than appearing once the
 *    first node finishes.
 * 2. Failing that, the first structure-bearing artifact the run produced.
 *    A standalone Tile run that takes no molecule input but emits one (a
 *    generator) still has a subject; so does a flow whose input row a
 *    retention sweep removed.
 *
 * Type-addressed at both steps via `isStructureBearingType` — the viewer's
 * own registry, not a second copy of it, and never a tile or node name.
 */
export function pickSubjectArtifact(
  artifacts: readonly RunVisualArtifact[]
): RunVisualArtifact | null {
  const structural = artifacts.filter((a) => isStructureBearingType(a.typed_object));
  return (
    structural.find((a) => a.origin === "input") ??
    structural.find((a) => a.origin === "produced") ??
    null
  );
}

/**
 * The structure-bearing artifacts a given flow node CONSUMED, oldest first.
 *
 * "What did this step actually operate on" (flowstack#956) — which the
 * run-level subject cannot answer, because it is the same for every step. At
 * `fragment` this is the parent ligand; at `torsion_fit` it is a fragment.
 *
 * Type-addressed after filtering, never before: the node filter narrows WHICH
 * artifacts are candidates, and `isStructureBearingType` still decides which
 * of them can be drawn. No tile name reaches the decision.
 *
 * A FANNED node consumes a different artifact per child (one fragment each),
 * so this can legitimately return several. The caller shows the first and says
 * how many there are rather than silently picking one — an unlabelled "a
 * fragment" would be a claim the data does not support.
 */
export function nodeInputArtifacts(
  artifacts: readonly RunVisualArtifact[],
  nodeName: string
): RunVisualArtifact[] {
  return artifacts.filter(
    (a) => (a.consumed_by_nodes ?? []).includes(nodeName) && isStructureBearingType(a.typed_object)
  );
}

/** Whether any artifact in the index unlocks the torsion-plot view. */
export function hasTorsionData(artifacts: readonly RunVisualArtifact[]): boolean {
  return artifacts.some((a) => TORSION_VIEW_TYPES.has(a.typed_object));
}

/**
 * Build the selector's view list.
 *
 * `runIsLive` decides `pending` vs `absent` and nothing else. It comes from
 * the run view's existing progress signal (the DAG summary state), so a view
 * stops claiming "not produced yet" the moment the flow can no longer produce
 * anything — at which point "this run produced none" is the honest sentence.
 */
export function deriveRunVisualViews(args: {
  visuals: RunVisualsResponse | undefined;
  runIsLive: boolean;
  /**
   * The selected flow node, if any (flowstack#956). With one selected the
   * molecule view PINS to that step's input; with none it falls back to the
   * run's subject, so deselecting always restores the run-level view rather
   * than stranding the user on a step.
   */
  selectedNodeName?: string | null;
}): RunVisualView[] {
  const { visuals, runIsLive, selectedNodeName = null } = args;
  const artifacts = visuals?.artifacts ?? [];
  const nodeInputs =
    selectedNodeName === null ? [] : nodeInputArtifacts(artifacts, selectedNodeName);
  // A selected step with no renderable input is NOT a reason to silently
  // show the run subject instead: that would claim the step operated on the
  // ligand when it may have consumed a force field or a topology. Fall back
  // only when nothing is selected.
  const pinned = selectedNodeName !== null;
  const subject = pinned ? (nodeInputs[0] ?? null) : pickSubjectArtifact(artifacts);
  const torsion = hasTorsionData(artifacts);
  // An index that has not loaded yet is not evidence of absence. Treat it as
  // pending regardless of `runIsLive` so a terminal run does not flash "this
  // run produced no molecule" before its first response lands.
  const unloaded = visuals === undefined;
  const notReady: RunVisualAvailability = unloaded || runIsLive ? "pending" : "absent";

  return RUN_VISUAL_VIEW_ORDER.map((id) => {
    const ready = id === "molecule" ? subject !== null : torsion;
    return {
      id,
      label: VIEW_LABELS[id],
      availability: ready ? "ready" : notReady,
      artifact: id === "molecule" ? subject : null,
      pinnedToNode: id === "molecule" && pinned ? selectedNodeName : null,
      siblingInputCount: id === "molecule" && pinned ? nodeInputs.length : 0,
    };
  });
}

/**
 * The view to show, given the user's pick.
 *
 * Falls back to the first `ready` view — and finally to the first view at all
 * — so a selection can never strand the panel on a view whose data has gone
 * (a regenerate, or a resume that superseded an artifact). Mirrors the
 * Analysis tab's `selectionIsCurrent` reconciliation for the same reason.
 */
export function resolveSelectedView(
  views: readonly RunVisualView[],
  selected: RunVisualViewId | null
): RunVisualView {
  const picked = views.find((v) => v.id === selected && v.availability === "ready");
  return picked ?? views.find((v) => v.availability === "ready") ?? views[0];
}

/**
 * A stable key over the run's PROGRESS, for `useRunVisuals`.
 *
 * This is what makes the panel progressive without a second polling loop: the
 * DAG query already re-reads every 5s while the run is active, and every one
 * of its responses carries each node's status. Folding those into a key means
 * the visuals index re-reads exactly when a node moved and never otherwise —
 * so a quiet flow, and a terminal one (whose DAG poll has stopped), cost
 * nothing.
 *
 * Node NAME is included, not just the status: a node appearing at all (a
 * fanout expanding) is progress the status list alone would miss when the new
 * node's status happens to match an existing one.
 *
 * `updated_at` is included for the case name/status/child_count cannot see
 * (Rupert PR-955 r0): while a `torsion_fit` FANOUT drains, the aggregate node
 * stays `running` with a stable `child_count` as its children complete one by
 * one — so a key without it would go unchanged for the entire stretch during
 * which the torsion plots are actually arriving, which is the whole point of
 * the surface. The backend aggregates the newest child transition into the
 * node's `updated_at`, so it moves on exactly those completions.
 */
export function runProgressKey(
  nodes:
    | readonly {
        name: string;
        status: string | null;
        child_count?: number;
        updated_at?: string | null;
      }[]
    | undefined
): string {
  if (nodes === undefined) return "unloaded";
  return nodes
    .map((n) => `${n.name}:${n.status ?? "-"}:${n.child_count ?? 0}:${n.updated_at ?? "-"}`)
    .join("|");
}
